/**
 * PagoUrgenteModal — modal reutilizable para suscripciones por vencer / vencidas / suspendidas.
 *
 * Variantes:
 *   - 'por_vencer'   → naranja, advertencia (D-3..D-1)
 *   - 'vencida'      → rojo, urgente pero accesible (D+0..D+9, en gracia)
 *   - 'suspendida'   → rojo fuerte, acceso bloqueado (D+10+)
 *
 * Props:
 *   - open: boolean
 *   - onClose: () => void
 *   - variant: 'por_vencer' | 'vencida' | 'suspendida'
 *   - fechaVencimiento?: string (formateada, ej: "23/03/2026")
 *   - diasRestantesGracia?: number (cuántos días faltan para suspensión, solo en 'vencida')
 *   - onPagar: () => void  (callback que dispara el flujo de pago de factura mensual)
 *   - onDebitoAutomatico?: () => void  (opcional, callback para adhesión a débito automático)
 *   - debitoAutomaticoActivo?: boolean (si ya tiene débito automático no mostrar la promo)
 *   - loadingPago?: boolean
 *   - loadingDebito?: boolean
 */
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { AlertTriangle, Clock, Lock, CreditCard, Loader2, Sparkles, Gem } from 'lucide-react';

const VARIANT_CONFIG = {
  por_vencer: {
    icon: Clock,
    iconColor: 'text-orange-500',
    titleColor: 'text-orange-700',
    bgGradient: 'from-orange-50 to-amber-50',
    borderColor: 'border-orange-200',
    title: '⚠️ Tu suscripción está por vencer',
    description: (fecha) =>
      fecha ? `Tu suscripción vence el ${fecha}. Pagá ahora para evitar interrupciones en tu acceso.` : 'Tu suscripción vence pronto.',
    ctaText: 'Pagar Ahora',
    ctaColor: 'bg-orange-500 hover:bg-orange-600',
  },
  vencida: {
    icon: AlertTriangle,
    iconColor: 'text-red-500',
    titleColor: 'text-red-700',
    bgGradient: 'from-red-50 to-orange-50',
    borderColor: 'border-red-200',
    title: '⏰ Tu suscripción ha vencido',
    description: () =>
      'Tu suscripción venció. Regularizá el pago para mantener el acceso sin interrupciones a la documentación técnica.',
    ctaText: 'Renovar Suscripción',
    ctaColor: 'bg-red-500 hover:bg-red-600',
  },
  suspendida: {
    icon: Lock,
    iconColor: 'text-red-600',
    titleColor: 'text-red-800',
    bgGradient: 'from-red-100 to-red-50',
    borderColor: 'border-red-300',
    title: '🔴 Suscripción Suspendida',
    description: () =>
      'Tu cuenta fue pasada a Status Report. Perdiste acceso a la documentación técnica y la impresión. Pagá para reactivar tu suscripción al instante.',
    ctaText: 'Reactivar Mi Cuenta',
    ctaColor: 'bg-red-600 hover:bg-red-700',
  },
};

export const PagoUrgenteModal = ({
  open,
  onClose,
  variant = 'por_vencer',
  fechaVencimiento,
  diasRestantesGracia,
  onPagar,
  onDebitoAutomatico,
  debitoAutomaticoActivo = false,
  onPagarAnual,
  ahorroAnualUsd,
  ahorroAnualPorcentaje,
  precioAnualUsd,
  loadingPago = false,
  loadingDebito = false,
  loadingAnual = false,
}) => {
  const cfg = VARIANT_CONFIG[variant] || VARIANT_CONFIG.por_vencer;
  const Icon = cfg.icon;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent
        className="sm:max-w-md max-h-[90vh] overflow-y-auto p-0"
        data-testid="pago-urgente-modal"
        // Suspendida: no permitir cerrar con backdrop ni Esc
        onPointerDownOutside={(e) => variant === 'suspendida' && e.preventDefault()}
        onEscapeKeyDown={(e) => variant === 'suspendida' && e.preventDefault()}
      >
        {/* Header con icono */}
        <div className={`bg-gradient-to-br ${cfg.bgGradient} px-6 pt-6 pb-4 border-b ${cfg.borderColor}`}>
          <DialogHeader>
            <div className="flex justify-center mb-3">
              <div className={`p-3 rounded-full bg-white shadow-md ${cfg.iconColor}`}>
                <Icon className="h-8 w-8" />
              </div>
            </div>
            <DialogTitle className={`text-xl text-center ${cfg.titleColor}`}>
              {cfg.title}
            </DialogTitle>
            <DialogDescription className="text-center text-slate-700 mt-2">
              {cfg.description(fechaVencimiento, diasRestantesGracia)}
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {/* CTA principal */}
          <Button
            onClick={onPagar}
            disabled={loadingPago}
            className={`w-full ${cfg.ctaColor} text-white font-bold py-6 text-base shadow-lg`}
            data-testid="pago-urgente-cta-pagar"
          >
            {loadingPago ? (
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
            ) : (
              <CreditCard className="h-5 w-5 mr-2" />
            )}
            {cfg.ctaText}
          </Button>

          {/* Promo PAGO ANUAL (Fase D) — siempre visible si onPagarAnual está disponible y hay ahorro */}
          {onPagarAnual && ahorroAnualUsd > 0 && (
            <div className="bg-gradient-to-br from-purple-600 via-fuchsia-600 to-pink-600 rounded-lg p-4 text-white shadow-lg relative overflow-hidden" data-testid="pago-urgente-promo-anual">
              <div className="absolute -top-2 -right-2 opacity-20">
                <Sparkles className="h-16 w-16" />
              </div>
              <div className="relative z-10">
                <div className="flex items-start gap-2 mb-2">
                  <Gem className="h-5 w-5 text-purple-100 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-bold text-sm leading-tight">
                      💎 ¿Sabías que pagando anual ahorrás US$ {ahorroAnualUsd.toLocaleString()}?
                    </p>
                    <p className="text-xs text-purple-100 mt-1 leading-snug">
                      Pagá <strong>US$ {precioAnualUsd.toLocaleString()}</strong> una sola vez y olvidate por 12 meses.
                      {ahorroAnualPorcentaje ? ` Ahorro del ${ahorroAnualPorcentaje}% vs mensual.` : ''}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={onPagarAnual}
                  disabled={loadingAnual}
                  className="w-full mt-2 bg-white text-purple-700 hover:bg-purple-50 font-semibold"
                  data-testid="pago-urgente-cta-anual"
                >
                  {loadingAnual ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Gem className="h-4 w-4 mr-2" />}
                  Pagar Anual y Ahorrar
                </Button>
              </div>
            </div>
          )}

          {/* Promo débito automático (solo si NO tiene débito activo) */}
          {!debitoAutomaticoActivo && onDebitoAutomatico && (
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-4">
              <div className="flex items-start gap-2 mb-2">
                <Sparkles className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-emerald-900 text-sm leading-tight">
                    Activá débito automático
                  </p>
                  <p className="text-xs text-emerald-700 mt-1 leading-snug">
                    Olvidate de los vencimientos y accedé a descuentos exclusivos.
                  </p>
                </div>
              </div>
              <Button
                onClick={onDebitoAutomatico}
                disabled={loadingDebito}
                variant="outline"
                className="w-full mt-2 border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                data-testid="pago-urgente-cta-debito"
              >
                {loadingDebito ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Sparkles className="h-4 w-4 mr-2" />
                )}
                Activar débito automático
              </Button>
            </div>
          )}

          {/* Botón cerrar (solo si NO está suspendida) */}
          {variant !== 'suspendida' && (
            <button
              onClick={onClose}
              className="w-full text-center text-sm text-slate-500 hover:text-slate-700 transition-colors py-2"
              data-testid="pago-urgente-cta-cerrar"
            >
              {variant === 'vencida' ? 'Pagar más tarde' : 'Recordarme luego'}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PagoUrgenteModal;
